'use client';

import { FC, forwardRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import { ImageComponent } from '../Image';
import { ModalSelect } from '../ModalSelect';
import { Language, useLanguages } from '@/hooks/languages';
import { useLanguageChange } from '@/hooks/translation';
import { useComponentVisible } from '@/hooks/visibility';
import { useCommonStore } from '@/store';

import {
  ScSwitchLanguage,
  ScSwitchLanguageButton,
  ScSwitchLanguageSelect,
} from './styled';

export const SwitchLanguage: FC = () => {
  const {
    i18n: { language: currentLang },
  } = useTranslation();

  const isMobileDevice = useCommonStore.use.isMobileDevice();
  const [changeLanguage, pending] = useLanguageChange();

  const { ref, openerRef, isComponentVisible, setIsComponentVisible } =
    useComponentVisible(false);

  const { images } = useTheme();

  const handleOpen = () => {
    setIsComponentVisible(!isComponentVisible);
  };

  const handleChangeLanguage = async (lang: string | number) => {
    if (typeof lang !== 'string') return;

    await changeLanguage(lang);
    window.location.reload();
  };

  const languages = useLanguages();

  const availableLanguages = useMemo(() => {
    return languages.filter(({ isHidden }) => !isHidden);
  }, [languages]);

  const currentLocale = useMemo(() => {
    return availableLanguages.find(({ value }) => value === currentLang);
  }, [availableLanguages, currentLang]);

  return (
    <ScSwitchLanguage $active={isComponentVisible}>
      <ScSwitchLanguageButton
        ref={openerRef}
        $active={isComponentVisible}
        onClick={handleOpen}
      >
        <div className="flag">
          <ImageComponent
            src={currentLocale?.icon || ''}
            width={22}
            height={22}
            alt="flag"
          />
        </div>
        <div className="value">{currentLocale?.name}</div>
        <div className="arrow">
          <images.dropdownArrow.component />
        </div>
      </ScSwitchLanguageButton>
      {isComponentVisible &&
        (isMobileDevice ? (
          <ModalSelect
            icon={<images.globeIcon.component />}
            title="Locale"
            options={availableLanguages}
            borderRoundIconOption={true}
            alignBottom
            onChange={handleChangeLanguage}
            onClose={handleOpen}
            cols={1}
            activeOption={currentLocale}
            innerAlignBottom
          />
        ) : (
          <SwitchLanguageSelect
            ref={ref}
            disabled={pending}
            onChange={handleChangeLanguage}
            availableLanguages={availableLanguages}
          />
        ))}
    </ScSwitchLanguage>
  );
};

type SwitchLanguageSelectProps = {
  disabled: boolean;
  onChange: (value: string) => void;
  availableLanguages: Language[];
};

const SwitchLanguageSelect = forwardRef<
  HTMLDivElement,
  SwitchLanguageSelectProps
>(({ disabled, onChange, availableLanguages }, ref) => {
  const {
    i18n: { language: currentLang },
  } = useTranslation();
  const { images } = useTheme();

  return (
    <ScSwitchLanguageSelect ref={ref}>
      {availableLanguages.map(({ icon, name, value }) => (
        <button
          key={value}
          disabled={disabled}
          onClick={(e) => {
            e.preventDefault();
            onChange(value);
          }}
          className={currentLang === value ? 'active' : ''}
        >
          <div className="btn-content">
            <ImageComponent
              src={icon}
              width={24}
              height={24}
              alt={currentLang}
            />
            {name}
          </div>

          {currentLang === value && <images.tickCircleIcon.component />}
        </button>
      ))}
    </ScSwitchLanguageSelect>
  );
});
